'use client';

import { Download, Share, SquarePlus, Smartphone } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * Subtle, non-intrusive install callout near the bottom of the marketing
 * page -- inline content, not a popup, so it never interrupts a first-time
 * visitor arriving from an ad/share link/influencer profile. Hides entirely
 * once Ertzyx is already running standalone, and degrades gracefully (no
 * dead-end button) on browsers that offer neither the install-prompt API
 * nor iOS "Add to Home Screen".
 */
export default function PwaInstallSection() {
  const { isStandalone, canInstall, isIOSSafari, promptInstall } = usePwaInstall();
  const { t } = useTranslation();

  if (isStandalone) return null;
  if (!canInstall && !isIOSSafari) return null;

  return (
    <section className="relative overflow-hidden bg-[var(--public-pearl)] py-16">
      <div className="relative mx-auto max-w-screen-xl px-6 lg:px-10 2xl:px-16">
        <div className="landing-install-panel mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-2xl p-8 text-center sm:flex-row sm:text-left">
          <div className="landing-install-icon flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl">
            <Smartphone size={26} aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold tracking-tight text-[var(--public-text)]">
              {t('pwa.landing_title' as any)}
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--public-text-muted)]">
              {t('pwa.landing_description' as any)}
            </p>

            {isIOSSafari && !canInstall ? (
              <ol className="mt-4 flex flex-col gap-1.5 text-sm font-medium text-[var(--public-text)]">
                <li className="flex items-center gap-2 justify-center sm:justify-start">
                  <Share size={14} className="text-[var(--public-teal)]" aria-hidden="true" />
                  {t('pwa.ios_step_share' as any)}
                </li>
                <li className="flex items-center gap-2 justify-center sm:justify-start">
                  <SquarePlus size={14} className="text-[var(--public-teal)]" aria-hidden="true" />
                  {t('pwa.ios_step_add_home' as any)}
                </li>
                <li className="flex items-center gap-2 justify-center sm:justify-start">
                  <Download size={14} className="text-[var(--public-teal)]" aria-hidden="true" />
                  {t('pwa.ios_step_add' as any)}
                </li>
              </ol>
            ) : (
              <button
                type="button"
                onClick={() => void promptInstall()}
                className="landing-install-button mt-4 inline-flex items-center gap-2 rounded-[10px] px-5 py-2.5 text-sm font-bold"
              >
                <Download size={15} aria-hidden="true" />
                {t('pwa.landing_action' as any)}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
